import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Dialog, RadioGroup } from "@headlessui/react";
import { toast } from "react-toastify";
import { sdkWrapperURL } from "utils/api-url-list";

const SelectOption = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [currentPage, setPage] = useState(() => {
    if (state?.patientData?.age?.length > 0 && state?.patientData?.gender?.length > 0)
      return "scanType";
    else return "patientData";
  });
  const [patientData, setPatientData] = useState(
    () =>
      state?.patientData ?? {
        patientID: "",
        name: "",
        age: "",
        gender: "",
        height: "",
        weight: "",
        waist: "",
      }
  );
  const [error, setError] = useState("");
  const [scanType, setScanType] = useState("");
  const [scanDuration, setScanDuration] = useState(60);
  const [isModalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const verifyPatientData = e => {
    try {
      e.preventDefault();
      setError("");
      if (!(patientData.age?.length > 0 && /^\d{1,3}$/.test(patientData.age)))
        throw new Error("Please enter a valid Age");
      if (patientData.age < 18) throw new Error("Age must be at least 18");
      else if (patientData.age > 110) throw new Error("Age can be at most 110");
      if (!(patientData.gender?.length > 0)) throw new Error("Gender must be selected");
      if (patientData.height?.length > 0) {
        if (!/^\d{1,3}(\.\d{1,2})?$/.test(patientData.height))
          throw new Error("Please enter a valid Height");
        if (patientData.height < 30 || patientData.height > 250)
          throw new Error("Height must be between 30 and 250 cms");
      }
      if (patientData.weight?.length > 0) {
        if (!/^\d{1,3}(\.\d{1,2})?$/.test(patientData.weight))
          throw new Error("Please enter a valid Weight");
        if (patientData.weight < 20 || patientData.weight > 220)
          throw new Error("Weight must be between 20 and 220 kgs");
      }
      if (patientData.waist?.length > 0) {
        if (!/^\d{1,2}$/.test(patientData.waist))
          throw new Error("Please enter a valid Waist length");
        if (patientData.waist < 10 || patientData.waist > 80)
          throw new Error("Waist length must be between 10 and 80 inches");
      }
      navigate(".", { replace: true, state: { patientData } });
      setPage("scanType");
    } catch (err) {
      setError(err.message);
    }
  };

  const getScanToken = async e => {
    try {
      e.preventDefault();
      setLoading(true);
      const resp = await fetch(sdkWrapperURL("/v2/users/sdk/test_api_private"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: localStorage.getItem("token"),
        },
        body: JSON.stringify({
          action: "create_scan_UL",
          employee_id: localStorage.getItem("emp_id"),
        }),
      });
      const resp_json = await resp.json();
      if (resp_json.statusCode?.toString().startsWith("2")) {
        const state = {
          patientData,
          scan_token: resp_json.scan_token,
          scan_duration: scanDuration * 1000,
        };
        if (scanType === "face") navigate("/face-scan", { state });
        else if (scanType === "finger") navigate("/finger-scan", { state });
      } else throw new Error(resp_json.message);
    } catch (err) {
      toast(err.message, {
        type: "error",
        position: "top-center",
        hideProgressBar: false,
        draggable: false,
        pauseOnHover: false,
        pauseOnFocusLoss: false,
        closeOnClick: false,
        autoClose: 3000,
        onClose: () => navigate("/", { replace: true }),
      });
    } finally {
      setLoading(false);
    }
  };

  return currentPage === "patientData" ? (
    <div>
      <h1 className="mb-6 text-slate-700 text-center text-3xl font-medium">Patient Data</h1>
      <form onSubmit={verifyPatientData}>
        <div className="mb-3">
          <label htmlFor="patientID" className="block mb-1.5 text-slate-700">
            Patient ID
          </label>
          <input
            className="block w-full px-3 py-1.5 rounded-md outline-none border border-neutral-300 bg-neutral-100 text-neutral-700"
            id="patientID"
            name="patientID"
            type="text"
            autoComplete="off"
            disabled
            defaultValue={patientData.patientID?.length > 0 ? patientData.patientID : "--"}
          />
        </div>
        <div className="mb-3">
          <label htmlFor="name" className="block mb-1.5 text-slate-700">
            Patient's Name
          </label>
          <input
            className="block w-full px-3 py-1.5 rounded-md outline-none border border-neutral-300 bg-white text-neutral-700 capitalize"
            id="name"
            name="name"
            type="text"
            autoComplete="off"
            value={patientData.name ?? ""}
            onChange={e => setPatientData(d => ({ ...d, name: e.target.value }))}
          />
        </div>
        <div className="mb-3">
          <label htmlFor="age" className="block mb-1.5 text-slate-700">
            Patient's Age<span style={{ color: "red" }}>*</span>
          </label>
          <input
            className="block w-full px-3 py-1.5 rounded-md outline-none border border-neutral-300 bg-white text-neutral-700"
            id="age"
            name="age"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            value={patientData.age}
            onChange={e => setPatientData(d => ({ ...d, age: e.target.value }))}
          />
        </div>
        <fieldset className="mb-3 p-2 pt-1 grid grid-cols-3 gap-3 rounded-md border border-neutral-300">
          <legend className="px-1.5 text-slate-700">
            Gender<span style={{ color: "red" }}>*</span>
          </legend>
          <div>
            <input
              className="hidden peer"
              id="gender-male"
              name="gender"
              type="radio"
              value="male"
              checked={patientData.gender === "male"}
              onChange={e => setPatientData(d => ({ ...d, gender: e.target.value }))}
            />
            <label
              htmlFor="gender-male"
              className="p-2 flex flex-col items-center justify-center rounded-xl border border-neutral-300 bg-white text-slate-700 peer-checked:font-medium peer-checked:border-slate-700 peer-checked:bg-slate-100"
            >
              <svg
                className="flex-shrink-0 h-6 w-6 p-px rounded-full border border-slate-500 bg-gradient-to-br from-slate-50 to-slate-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 40 40"
              >
                <path d="M12.15,13.82c0,0-2.26-7.65,5.72-10.63c1.69-0.4,6.44-1.69,10.23,3.87c0.97,2.09,1.29,3.22,0.97,6.85" />
                <path
                  d="M14.01,11.97c0,0,4.51,0.4,4.91-3.22c0.81,1.77,1.13,3.63,8.06,3.38c0,1.53,1.37,9.1-6.28,9.83
	C19.16,21.8,13.6,22.04,14.01,11.97z"
                />
                <path d="M13.93,13.02c0,0-2.34-0.16-2.26,2.26c0.16,1.13,0.73,2.26,2.66,1.85" />
                <path d="M26.9,13.1c0,0,2.82,0.08,2.42,2.26c-0.16,0.97-0.89,2.18-2.58,1.85" />
                <line x1="17.23" y1="20.91" x2="17.23" y2="24.38" />
                <line x1="23.91" y1="20.99" x2="23.91" y2="24.54" />
                <polygon points="15.62,23.01 20.53,27.76 18.52,30.02 13.36,25.1 " />
                <polygon points="25.45,22.93 20.53,27.76 22.71,30.1 27.62,25.18 " />
                <path d="M13.36,25.1c0,0-10.15,0.89-9.18,12.65c4.67,0,32.71,0.08,32.71,0.08s1.85-11.2-9.26-12.65" />
                <line x1="10.94" y1="37.83" x2="10.86" y2="34.77" />
                <line x1="30.2" y1="37.91" x2="30.2" y2="34.85" />
              </svg>
              <span className="mt-1">Male</span>
            </label>
          </div>
          <div>
            <input
              className="hidden peer"
              id="gender-female"
              name="gender"
              type="radio"
              value="female"
              checked={patientData.gender === "female"}
              onChange={e => setPatientData(d => ({ ...d, gender: e.target.value }))}
            />
            <label
              htmlFor="gender-female"
              className="p-2 flex flex-col items-center justify-center rounded-xl border border-neutral-300 bg-white text-slate-700 peer-checked:font-medium peer-checked:border-slate-700 peer-checked:bg-slate-100"
            >
              <svg
                className="flex-shrink-0 h-6 w-6 p-px rounded-full border border-slate-500 bg-gradient-to-br from-slate-50 to-slate-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 40 40"
              >
                <path
                  d="M8.74,23.67c0,0,0.08-7.58,0.08-8.92S8.51,3.69,19.88,2.66c1.58,0.16,10.42,0.47,11.14,11.61
	c-0.08,3.16-0.08,9.32-0.08,9.32"
                />
                <path
                  d="M13.48,11.9c0,0,7.42,0.87,8.13-3.63c0.55,2.05,0.55,3.16,4.42,3.55c0,2.21,1.34,8.53-5.84,9.71
	C18.61,21.46,12.61,21.06,13.48,11.9z"
                />
                <path d="M13.32,12.93c0,0-2.29,0-2.13,2.13c0.16,1.03,0.63,1.97,2.53,1.74" />
                <path d="M26.12,13.01c0,0,2.45,0.08,2.37,1.97c-0.08,1.18-1.42,2.21-2.69,1.82" />
                <line x1="16.56" y1="20.59" x2="16.48" y2="23.83" />
                <line x1="23.04" y1="20.67" x2="23.04" y2="23.98" />
                <polygon points="12.77,24.7 15.06,22.56 19.88,27.38 17.9,29.43 " />
                <polygon points="19.88,27.38 24.77,22.56 26.67,24.62 22.01,29.43 " />
                <path d="M12.77,24.7c0,0-9.56,0.24-9.08,12.32c4.5,0.08,32.22,0,32.22,0s1.66-10.82-9.24-12.4" />
                <line x1="29.28" y1="37.17" x2="29.28" y2="34.17" />
                <line x1="10.32" y1="36.94" x2="10.24" y2="34.33" />
                <circle cx="19.84" cy="31.57" r="0.59" />
                <circle cx="19.84" cy="34.38" r="0.59" />
              </svg>
              <span className="mt-1">Female</span>
            </label>
          </div>
          <div>
            <input
              className="hidden peer"
              id="gender-others"
              name="gender"
              type="radio"
              value="others"
              checked={patientData.gender === "others"}
              onChange={e => setPatientData(d => ({ ...d, gender: e.target.value }))}
            />
            <label
              htmlFor="gender-others"
              className="p-2 flex flex-col items-center justify-center rounded-xl border border-neutral-300 bg-white text-slate-700 peer-checked:font-medium peer-checked:border-slate-700 peer-checked:bg-slate-100"
            >
              <svg
                className="flex-shrink-0 h-6 w-6 p-px rounded-full border border-slate-500 bg-gradient-to-br from-slate-50 to-slate-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 40 40"
              >
                <ellipse cx="20.62" cy="19.47" rx="10.04" ry="10" />
                <polyline points="14.19,11.83 5.6,3.41 5.69,10.56 " />
                <line x1="5.6" y1="3.41" x2="13.11" y2="3.41" />
                <line x1="14.37" y1="6.03" x2="8.4" y2="12.19" />
                <polyline points="27.22,12.1 36,3.14 27.95,3.23 " />
                <line x1="36" y1="3.14" x2="35.82" y2="10.38" />
                <line x1="20.62" y1="29.47" x2="20.62" y2="38.52" />
                <line x1="15.37" y1="33.99" x2="25.68" y2="34.08" />
              </svg>
              <span className="mt-1">Other</span>
            </label>
          </div>
        </fieldset>
        <div className="mb-3">
          <label htmlFor="height" className="block mb-1.5 text-slate-700">
            Height (cms)
          </label>
          <input
            className="block w-full px-3 py-1.5 rounded-md outline-none border border-neutral-300 bg-white text-neutral-700"
            id="height"
            name="height"
            type="text"
            inputMode="decimal"
            autoComplete="off"
            value={patientData.height ?? ""}
            onChange={e => setPatientData(d => ({ ...d, height: e.target.value }))}
          />
        </div>
        <div className="mb-3">
          <label htmlFor="weight" className="block mb-1.5 text-slate-700">
            Weight (kgs)
          </label>
          <input
            className="block w-full px-3 py-1.5 rounded-md outline-none border border-neutral-300 bg-white text-neutral-700"
            id="weight"
            name="weight"
            type="text"
            inputMode="decimal"
            autoComplete="off"
            value={patientData.weight ?? ""}
            onChange={e => setPatientData(d => ({ ...d, weight: e.target.value }))}
          />
        </div>
        <div className="mb-3">
          <label htmlFor="waist" className="block mb-1.5 text-slate-700">
            Waist (in)
          </label>
          <input
            className="block w-full px-3 py-1.5 rounded-md outline-none border border-neutral-300 bg-white text-neutral-700"
            id="waist"
            name="waist"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            value={patientData.waist ?? ""}
            onChange={e => setPatientData(d => ({ ...d, waist: e.target.value }))}
          />
        </div>
        {error.length > 0 && (
          <p className="mt-3 px-3 py-1.5 rounded-md flex items-center bg-red-200 text-red-600">
            <svg className="flex-shrink-0 h-6 w-6" viewBox="0 0 48 48" fill="currentColor">
              <path d="M24 34q.7 0 1.175-.475.475-.475.475-1.175 0-.7-.475-1.175Q24.7 30.7 24 30.7q-.7 0-1.175.475-.475.475-.475 1.175 0 .7.475 1.175Q23.3 34 24 34Zm-1.35-7.65h3V13.7h-3ZM24 44q-4.1 0-7.75-1.575-3.65-1.575-6.375-4.3-2.725-2.725-4.3-6.375Q4 28.1 4 23.95q0-4.1 1.575-7.75 1.575-3.65 4.3-6.35 2.725-2.7 6.375-4.275Q19.9 4 24.05 4q4.1 0 7.75 1.575 3.65 1.575 6.35 4.275 2.7 2.7 4.275 6.35Q44 19.85 44 24q0 4.1-1.575 7.75-1.575 3.65-4.275 6.375t-6.35 4.3Q28.15 44 24 44Zm.05-3q7.05 0 12-4.975T41 23.95q0-7.05-4.95-12T24 7q-7.05 0-12.025 4.95Q7 16.9 7 24q0 7.05 4.975 12.025Q16.95 41 24.05 41ZM24 24Z" />
            </svg>
            <span className="ml-2 font-light">{error}</span>
          </p>
        )}
        <div className="mt-6 grid grid-cols-2 gap-6 px-3">
          <button
            type="button"
            onClick={() => navigate("/", { replace: true })}
            className="px-3 py-1.5 rounded-full bg-white text-slate-700 border border-slate-700 text-xl font-medium"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-3 py-1.5 rounded-full bg-slate-700 text-white border border-slate-700 text-xl font-medium"
          >
            Next
          </button>
        </div>
      </form>
    </div>
  ) : currentPage === "scanType" ? (
    <div>
      <RadioGroup
        as="fieldset"
        value={scanDuration}
        onChange={setScanDuration}
        className="mb-6 p-2 pt-1 grid grid-cols-2 gap-6 rounded-md border border-neutral-300"
      >
        <RadioGroup.Label as="legend" className="px-1.5 text-slate-700">
          Scan Duration
        </RadioGroup.Label>
        <RadioGroup.Option
          value={60}
          className={({ checked }) =>
            `py-3 px-6 flex flex-col items-center justify-between space-y-3 rounded-lg border ${
              checked ? "border-slate-700 bg-slate-500/5" : "border-slate-300"
            }`
          }
        >
          {({ checked }) => (
            <>
              <svg
                className={`w-16 ${checked ? "text-slate-900" : "text-slate-700 p-2"}`}
                viewBox="0 0 57.2 57.2"
              >
                <circle fill="#E6EAED" cx="28.6" cy="29.2" r="26" />
                <circle fill="#FFFFFF" cx="28.6" cy="29.2" r="18" />
                <path
                  fill="#424A60"
                  d="M12.6,57.2c-0.3,0-0.5-0.1-0.7-0.3c-0.4-0.4-0.4-1,0-1.4l3.9-3.9c0.4-0.4,1-0.4,1.4,0s0.4,1,0,1.4l-3.9,3.9  C13.1,57.1,12.8,57.2,12.6,57.2z"
                />
                <path
                  fill="#424A60"
                  d="M44.6,57.2c-0.3,0-0.5-0.1-0.7-0.3l-4-4c-0.4-0.4-0.4-1,0-1.4s1-0.4,1.4,0l4,4c0.4,0.4,0.4,1,0,1.4  C45.1,57.1,44.8,57.2,44.6,57.2z"
                />
                <path
                  fill="#939CB2"
                  d="M46.7,11.4c-0.3,0-0.5-0.1-0.7-0.3c-0.4-0.4-0.4-1,0-1.4l3-3c0.4-0.4,1-0.4,1.4,0s0.4,1,0,1.4l-3,3  C47.2,11.3,46.9,11.4,46.7,11.4z"
                />
                <path
                  fill="#939CB2"
                  d="M10.5,11.4c-0.3,0-0.5-0.1-0.7-0.3l-3-3c-0.4-0.4-0.4-1,0-1.4s1-0.4,1.4,0l3,3c0.4,0.4,0.4,1,0,1.4  C11,11.3,10.7,11.4,10.5,11.4z"
                />
                <path
                  fill="currentColor"
                  d="M28.6,57.2c-15.4,0-28-12.6-28-28s12.6-28,28-28s28,12.6,28,28S44,57.2,28.6,57.2z M28.6,5.2  c-13.2,0-24,10.8-24,24s10.8,24,24,24s24-10.8,24-24S41.8,5.2,28.6,5.2z"
                />
                <path
                  fill="#939CB2"
                  d="M28.6,6c-0.6,0-1,0.4-1,1v1c0,0.6,0.4,1,1,1s1-0.4,1-1V7C29.6,6.5,29.1,6,28.6,6z"
                />
                <path
                  fill="#939CB2"
                  d="M28.6,49c-0.6,0-1,0.4-1,1v1c0,0.6,0.4,1,1,1s1-0.4,1-1v-1C29.6,49.5,29.1,49,28.6,49z"
                />
                <path
                  fill="#939CB2"
                  d="M50.6,28h-1c-0.6,0-1,0.4-1,1s0.4,1,1,1h1c0.6,0,1-0.4,1-1S51.1,28,50.6,28z"
                />
                <path
                  fill="#939CB2"
                  d="M7.6,28h-1c-0.6,0-1,0.4-1,1s0.4,1,1,1h1c0.6,0,1-0.4,1-1S8.1,28,7.6,28z"
                />
                <path
                  fill="#939CB2"
                  d="M43.4,12.8l-0.7,0.7c-0.4,0.4-0.4,1,0,1.4c0.2,0.2,0.5,0.3,0.7,0.3s0.5-0.1,0.7-0.3l0.7-0.7   c0.4-0.4,0.4-1,0-1.4S43.8,12.4,43.4,12.8z"
                />
                <path
                  fill="#939CB2"
                  d="M13,43.2l-0.7,0.7c-0.4,0.4-0.4,1,0,1.4c0.2,0.2,0.5,0.3,0.7,0.3s0.5-0.1,0.7-0.3l0.7-0.7c0.4-0.4,0.4-1,0-1.4   S13.4,42.8,13,43.2z"
                />
                <path
                  fill="#939CB2"
                  d="M44.1,43.2c-0.4-0.4-1-0.4-1.4,0s-0.4,1,0,1.4l0.7,0.7c0.2,0.2,0.5,0.3,0.7,0.3s0.5-0.1,0.7-0.3   c0.4-0.4,0.4-1,0-1.4L44.1,43.2z"
                />
                <path
                  fill="#939CB2"
                  d="M13.7,12.8c-0.4-0.4-1-0.4-1.4,0s-0.4,1,0,1.4l0.7,0.7c0.2,0.2,0.5,0.3,0.7,0.3s0.5-0.1,0.7-0.3   c0.4-0.4,0.4-1,0-1.4L13.7,12.8z"
                />
                <path
                  fill="#D9DEE2"
                  d="M54.5,2.1c2.8,2.8,2.8,7.3,0,10.1L44.4,2.1C47.2-0.7,51.7-0.7,54.5,2.1z"
                />
                <path
                  fill="#D9DEE2"
                  d="M2.7,2.1c-2.8,2.8-2.8,7.3,0,10.1L12.7,2.1C10-0.7,5.4-0.7,2.7,2.1z"
                />
                <path
                  fill="#424A60"
                  d="M26.8,23.8c-0.2,0.1-0.4,0.2-0.6,0.2c-0.3,0-0.7-0.2-0.9-0.5l-0.7-1c-0.1-0.2-0.2-0.4-0.2-0.6   c0-0.4,0.2-0.7,0.5-0.9l3.2-2.3c0.4-0.3,0.7-0.5,1.2-0.5h0.9c0.6,0,1.1,0.5,1.1,1.1v14.6c0,0.6-0.5,1.1-1.1,1.1h-1.4   c-0.6,0-1.1-0.5-1.1-1.1V23.3L26.8,23.8z"
                />
                <path
                  fill="#424A60"
                  d="M23.9,42.3c0,0.1-0.1,0.3-0.3,0.3h-0.4c-0.1,0-0.3-0.1-0.3-0.3v-3.6c0-0.1,0.1-0.3,0.3-0.3h0.5   c0.1,0,0.2,0.1,0.3,0.2l1.2,1.6l1.1-1.6c0.1-0.1,0.2-0.2,0.3-0.2H27c0.1,0,0.3,0.1,0.3,0.3v3.6c0,0.1-0.1,0.3-0.3,0.3h-0.4   c-0.1,0-0.3-0.1-0.3-0.3v-2.4l-0.9,1.2c-0.1,0.1-0.2,0.2-0.4,0.2s-0.3-0.1-0.4-0.2l-0.9-1.2V42.3z"
                />
                <path
                  fill="#424A60"
                  d="M29.4,42.3c0,0.1-0.1,0.3-0.3,0.3h-0.4c-0.1,0-0.3-0.1-0.3-0.3v-3.6c0-0.1,0.1-0.3,0.3-0.3h0.4   c0.1,0,0.3,0.1,0.3,0.3V42.3z"
                />
                <path
                  fill="#424A60"
                  d="M33.9,38.4c0.1,0,0.3,0.1,0.3,0.3v3.6c0,0.1-0.1,0.3-0.3,0.3h-0.3c-0.1,0-0.3-0.1-0.3-0.2l-1.9-2.4v2.2   c0,0.1-0.1,0.3-0.3,0.3h-0.3c-0.1,0-0.3-0.1-0.3-0.3v-3.6c0-0.1,0.1-0.3,0.3-0.3h0.3c0.1,0,0.3,0.1,0.3,0.2l1.9,2.4v-2.3   c0-0.1,0.1-0.3,0.3-0.3H33.9z"
                />
              </svg>
              <span className="text-sm text-slate-700 font-medium">1 Minute</span>
            </>
          )}
        </RadioGroup.Option>
        <RadioGroup.Option
          value={120}
          className={({ checked }) =>
            `py-3 px-6 flex flex-col items-center justify-between space-y-3 rounded-lg border ${
              checked ? "border-slate-700 bg-slate-500/5" : "border-slate-300"
            }`
          }
        >
          {({ checked }) => (
            <>
              <svg
                className={`w-16 ${checked ? "text-slate-900" : "text-slate-700 p-2"}`}
                viewBox="0 0 57.2 57.2"
              >
                <circle fill="#E6EAED" cx="28.6" cy="29.2" r="26" />
                <circle fill="#FFFFFF" cx="28.6" cy="29.2" r="18" />
                <path
                  fill="#424A60"
                  d="M12.6,57.2c-0.3,0-0.5-0.1-0.7-0.3c-0.4-0.4-0.4-1,0-1.4l3.9-3.9c0.4-0.4,1-0.4,1.4,0s0.4,1,0,1.4l-3.9,3.9  C13.1,57.1,12.8,57.2,12.6,57.2z"
                />
                <path
                  fill="#424A60"
                  d="M44.6,57.2c-0.3,0-0.5-0.1-0.7-0.3l-4-4c-0.4-0.4-0.4-1,0-1.4s1-0.4,1.4,0l4,4c0.4,0.4,0.4,1,0,1.4  C45.1,57.1,44.8,57.2,44.6,57.2z"
                />
                <path
                  fill="#939CB2"
                  d="M46.7,11.4c-0.3,0-0.5-0.1-0.7-0.3c-0.4-0.4-0.4-1,0-1.4l3-3c0.4-0.4,1-0.4,1.4,0s0.4,1,0,1.4l-3,3  C47.2,11.3,46.9,11.4,46.7,11.4z"
                />
                <path
                  fill="#939CB2"
                  d="M10.5,11.4c-0.3,0-0.5-0.1-0.7-0.3l-3-3c-0.4-0.4-0.4-1,0-1.4s1-0.4,1.4,0l3,3c0.4,0.4,0.4,1,0,1.4  C11,11.3,10.7,11.4,10.5,11.4z"
                />
                <path
                  fill="currentColor"
                  d="M28.6,57.2c-15.4,0-28-12.6-28-28s12.6-28,28-28s28,12.6,28,28S44,57.2,28.6,57.2z M28.6,5.2  c-13.2,0-24,10.8-24,24s10.8,24,24,24s24-10.8,24-24S41.8,5.2,28.6,5.2z"
                />
                <path
                  fill="#939CB2"
                  d="M28.6,6c-0.6,0-1,0.4-1,1v1c0,0.6,0.4,1,1,1s1-0.4,1-1V7C29.6,6.5,29.1,6,28.6,6z"
                />
                <path
                  fill="#939CB2"
                  d="M28.6,49c-0.6,0-1,0.4-1,1v1c0,0.6,0.4,1,1,1s1-0.4,1-1v-1C29.6,49.5,29.1,49,28.6,49z"
                />
                <path
                  fill="#939CB2"
                  d="M50.6,28h-1c-0.6,0-1,0.4-1,1s0.4,1,1,1h1c0.6,0,1-0.4,1-1S51.1,28,50.6,28z"
                />
                <path
                  fill="#939CB2"
                  d="M7.6,28h-1c-0.6,0-1,0.4-1,1s0.4,1,1,1h1c0.6,0,1-0.4,1-1S8.1,28,7.6,28z"
                />
                <path
                  fill="#939CB2"
                  d="M43.4,12.8l-0.7,0.7c-0.4,0.4-0.4,1,0,1.4c0.2,0.2,0.5,0.3,0.7,0.3s0.5-0.1,0.7-0.3l0.7-0.7   c0.4-0.4,0.4-1,0-1.4S43.8,12.4,43.4,12.8z"
                />
                <path
                  fill="#939CB2"
                  d="M13,43.2l-0.7,0.7c-0.4,0.4-0.4,1,0,1.4c0.2,0.2,0.5,0.3,0.7,0.3s0.5-0.1,0.7-0.3l0.7-0.7c0.4-0.4,0.4-1,0-1.4   S13.4,42.8,13,43.2z"
                />
                <path
                  fill="#939CB2"
                  d="M44.1,43.2c-0.4-0.4-1-0.4-1.4,0s-0.4,1,0,1.4l0.7,0.7c0.2,0.2,0.5,0.3,0.7,0.3s0.5-0.1,0.7-0.3   c0.4-0.4,0.4-1,0-1.4L44.1,43.2z"
                />
                <path
                  fill="#939CB2"
                  d="M13.7,12.8c-0.4-0.4-1-0.4-1.4,0s-0.4,1,0,1.4l0.7,0.7c0.2,0.2,0.5,0.3,0.7,0.3s0.5-0.1,0.7-0.3   c0.4-0.4,0.4-1,0-1.4L13.7,12.8z"
                />
                <path
                  fill="#D9DEE2"
                  d="M54.5,2.1c2.8,2.8,2.8,7.3,0,10.1L44.4,2.1C47.2-0.7,51.7-0.7,54.5,2.1z"
                />
                <path
                  fill="#D9DEE2"
                  d="M2.7,2.1c-2.8,2.8-2.8,7.3,0,10.1L12.7,2.1C10-0.7,5.4-0.7,2.7,2.1z"
                />
                <path
                  fill="#424A60"
                  d="M33.8,33.9c0,0.6-0.5,1.1-1.1,1.1h-8.6c-0.6,0-1.1-0.5-1.1-1.1v-1.2c0-0.5,0.2-0.8,0.6-1.1   c2.7-2.6,5.9-5.5,5.9-8.3c0-1.2-0.6-1.7-1.5-1.7c-0.6,0-1.1,0.4-1.5,0.9c-0.3,0.5-0.6,0.7-1.1,0.7c-0.2,0-0.4,0-0.6-0.1l-1.2-0.5   c-0.4-0.2-0.6-0.5-0.6-0.9c0-0.1,0-0.3,0.1-0.5c0.8-2.1,2.7-3.3,5.3-3.3c3.3,0,4.9,2.2,4.9,4.5c0,4.7-4.4,8.1-5.6,9.1h5.1   c0.6,0,1.1,0.5,1.1,1.1V33.9z"
                />
                <path
                  fill="#424A60"
                  d="M23.9,42.3c0,0.1-0.1,0.3-0.3,0.3h-0.4c-0.1,0-0.3-0.1-0.3-0.3v-3.6c0-0.1,0.1-0.3,0.3-0.3h0.5   c0.1,0,0.2,0.1,0.3,0.2l1.2,1.6l1.1-1.6c0.1-0.1,0.2-0.2,0.3-0.2H27c0.1,0,0.3,0.1,0.3,0.3v3.6c0,0.1-0.1,0.3-0.3,0.3h-0.4   c-0.1,0-0.3-0.1-0.3-0.3v-2.4l-0.9,1.2c-0.1,0.1-0.2,0.2-0.4,0.2s-0.3-0.1-0.4-0.2l-0.9-1.2V42.3z"
                />
                <path
                  fill="#424A60"
                  d="M29.4,42.3c0,0.1-0.1,0.3-0.3,0.3h-0.4c-0.1,0-0.3-0.1-0.3-0.3v-3.6c0-0.1,0.1-0.3,0.3-0.3h0.4   c0.1,0,0.3,0.1,0.3,0.3V42.3z"
                />
                <path
                  fill="#424A60"
                  d="M33.9,38.4c0.1,0,0.3,0.1,0.3,0.3v3.6c0,0.1-0.1,0.3-0.3,0.3h-0.3c-0.1,0-0.3-0.1-0.3-0.2l-1.9-2.4v2.2   c0,0.1-0.1,0.3-0.3,0.3h-0.3c-0.1,0-0.3-0.1-0.3-0.3v-3.6c0-0.1,0.1-0.3,0.3-0.3h0.3c0.1,0,0.3,0.1,0.3,0.2l1.9,2.4v-2.3   c0-0.1,0.1-0.3,0.3-0.3H33.9z"
                />
              </svg>
              <span className="text-sm text-slate-700 font-medium">2 Minute</span>
            </>
          )}
        </RadioGroup.Option>
      </RadioGroup>
      <button
        type="button"
        onClick={() => {
          setScanType("face");
          setPage("startScan");
        }}
        className="w-full mb-8 p-4 pl-2 flex items-center justify-between space-x-2 rounded-3xl bg-neutral-100 text-slate-500 text-left"
      >
        <svg className="flex-shrink-0 w-20" fill="currentColor" viewBox="0 0 400 400">
          <path d="M102.947 1.832 C 89.883 6.284,79.885 17.743,77.356 31.161 C 76.261 36.970,76.261 363.030,77.356 368.839 C 79.922 382.454,90.517 394.366,103.676 398.430 C 111.631 400.887,288.369 400.887,296.324 398.430 C 309.483 394.366,320.078 382.454,322.644 368.839 C 323.160 366.101,323.438 320.697,323.438 238.920 L 323.438 113.210 321.520 111.293 C 318.983 108.756,315.392 108.756,312.855 111.293 L 310.938 113.210 310.938 230.824 L 310.938 348.438 231.214 348.438 L 151.491 348.438 149.574 350.355 C 147.037 352.892,147.037 356.483,149.574 359.020 L 151.491 360.938 231.321 360.938 L 311.150 360.938 310.650 364.585 C 309.023 376.453,299.558 385.772,287.335 387.539 C 280.923 388.467,119.077 388.467,112.665 387.539 C 100.442 385.772,90.977 376.453,89.350 364.585 L 88.850 360.938 108.914 360.938 L 128.977 360.938 130.895 359.020 C 133.432 356.483,133.432 352.892,130.895 350.355 L 128.977 348.438 109.020 348.438 L 89.063 348.438 89.063 191.939 C 89.063 19.764,88.722 31.663,93.865 23.900 C 96.698 19.623,101.872 15.540,107.031 13.510 C 111.099 11.909,275.948 10.903,286.838 12.412 C 296.783 13.790,304.374 19.252,308.461 27.971 L 310.547 32.422 310.938 61.443 C 311.379 94.272,311.277 93.750,317.252 93.750 C 323.413 93.750,323.438 93.622,323.438 61.778 C 323.438 23.680,321.145 16.321,306.159 6.313 C 296.063 -0.429,303.271 -0.003,199.722 0.020 L 108.203 0.040 102.947 1.832 M179.688 22.120 C 164.441 26.953,164.789 51.509,180.169 56.076 C 184.560 57.380,215.440 57.380,219.831 56.076 C 235.516 51.418,235.516 26.707,219.831 22.049 C 215.699 20.821,183.604 20.878,179.688 22.120 M217.614 35.511 C 224.063 41.961,219.045 44.531,200.000 44.531 C 182.899 44.531,180.469 43.851,180.469 39.063 C 180.469 38.164,181.332 36.566,182.386 35.511 C 185.390 32.508,214.610 32.508,217.614 35.511 M108.324 87.366 C 105.969 89.968,106.420 124.154,108.839 126.426 C 111.238 128.680,114.448 128.592,116.832 126.207 C 118.723 124.317,118.750 124.094,118.750 110.582 L 118.750 96.875 132.248 96.875 C 147.905 96.875,149.219 96.458,149.219 91.483 C 149.219 86.052,148.780 85.938,127.989 85.938 C 111.183 85.938,109.507 86.059,108.324 87.366 M249.730 87.366 C 247.880 89.411,248.088 93.363,250.147 95.282 C 251.734 96.760,252.849 96.875,265.577 96.874 L 279.297 96.873 279.055 108.397 C 278.922 114.736,279.030 121.084,279.294 122.505 C 280.288 127.845,285.538 129.923,288.926 126.317 C 291.356 123.730,291.593 89.400,289.196 87.230 C 286.920 85.170,251.608 85.292,249.730 87.366 M185.488 100.027 C 180.246 102.067,175.429 106.295,172.565 111.370 C 170.328 115.333,170.014 115.563,164.610 117.197 C 144.138 123.385,130.017 137.473,124.905 156.807 C 123.246 163.083,122.452 182.697,123.707 186.432 C 124.189 187.869,123.898 188.167,121.598 188.599 C 94.641 193.656,101.249 251.693,128.669 250.699 L 133.828 250.512 135.519 255.530 C 156.184 316.875,245.822 316.349,264.875 254.771 L 266.192 250.513 271.341 250.700 C 297.650 251.653,305.604 198.229,280.482 189.307 L 276.863 188.021 277.194 179.362 C 277.731 165.315,274.795 151.991,269.089 142.578 L 266.247 137.891 267.694 133.594 C 272.033 120.709,266.781 107.068,255.109 100.907 L 251.172 98.828 220.313 98.656 C 191.210 98.494,189.227 98.572,185.488 100.027 M250.061 112.714 C 256.228 116.869,258.238 123.026,255.448 129.213 C 252.183 136.455,252.423 140.272,256.546 146.646 C 266.064 161.360,267.299 181.877,259.961 203.400 C 256.990 212.116,256.081 209.411,255.861 191.201 C 255.475 159.271,250.133 153.721,225.670 159.835 C 208.005 164.250,196.057 164.433,179.297 160.548 C 162.632 156.685,159.709 156.431,155.671 158.491 C 147.771 162.521,143.819 177.087,145.068 197.577 C 146.104 214.581,140.072 207.269,136.700 187.434 C 131.480 156.724,143.456 136.409,171.641 128.165 C 177.182 126.544,182.031 123.088,182.031 120.760 C 182.031 118.906,186.502 113.581,189.156 112.274 C 193.909 109.933,246.498 110.313,250.061 112.714 M170.171 171.435 C 192.398 177.031,207.387 177.257,227.716 172.303 C 243.328 168.499,243.355 168.537,243.358 194.206 L 243.359 209.896 245.480 212.958 C 246.647 214.642,249.371 217.764,251.535 219.895 L 255.469 223.769 255.469 231.848 C 255.469 248.605,250.281 261.587,239.164 272.647 C 203.551 308.077,144.531 282.903,144.531 232.283 L 144.531 224.191 150.708 218.089 C 158.059 210.826,158.494 209.608,157.730 198.438 C 156.966 187.260,158.045 176.118,160.359 171.289 C 161.394 169.130,160.989 169.124,170.171 171.435 M170.782 187.603 C 163.522 188.629,160.769 191.421,162.037 196.474 C 162.923 200.005,165.756 201.120,171.447 200.177 C 174.544 199.664,177.557 199.615,179.423 200.048 C 186.223 201.626,190.625 199.540,190.625 194.741 C 190.625 188.875,182.493 185.950,170.782 187.603 M221.875 187.172 C 221.230 187.298,218.989 187.627,216.894 187.904 C 209.885 188.829,207.019 194.590,211.393 198.964 C 213.269 200.840,213.720 200.939,217.838 200.377 C 223.527 199.602,224.487 199.599,229.834 200.336 C 236.707 201.284,240.719 196.016,237.081 190.821 C 235.539 188.621,226.084 186.352,221.875 187.172 M128.784 206.250 C 132.147 216.706,133.428 236.797,130.795 237.807 C 119.264 242.232,111.462 212.501,121.661 202.996 C 125.965 198.985,126.530 199.243,128.784 206.250 M278.414 203.019 C 288.375 213.416,280.746 242.236,269.205 237.807 C 266.267 236.680,267.844 219.423,271.982 207.422 C 272.870 204.844,273.726 202.295,273.884 201.758 C 274.355 200.148,276.139 200.645,278.414 203.019 M168.750 212.500 C 166.470 214.780,166.496 223.351,168.789 225.426 C 174.434 230.535,181.583 223.344,178.910 215.245 C 177.447 210.813,171.928 209.322,168.750 212.500 M223.301 211.749 C 220.006 213.671,219.647 222.667,222.752 225.477 C 227.390 229.675,232.813 226.194,232.813 219.020 C 232.813 211.991,228.515 208.706,223.301 211.749 M192.276 239.648 C 186.216 244.680,191.230 251.097,200.879 250.658 C 209.661 250.260,213.479 244.800,208.348 239.980 C 205.916 237.695,194.902 237.468,192.276 239.648 M183.949 255.824 C 174.410 265.363,198.001 274.237,212.986 266.747 C 223.090 261.696,218.299 250.883,207.933 255.342 C 203.037 257.449,198.099 257.490,192.782 255.469 C 187.474 253.452,186.273 253.500,183.949 255.824 M108.730 265.871 C 106.300 268.457,106.063 302.788,108.460 304.957 C 109.704 306.083,112.262 306.250,128.261 306.250 C 148.905 306.250,149.219 306.165,149.219 300.569 C 149.219 295.749,147.809 295.313,132.248 295.313 L 118.750 295.313 118.750 281.605 C 118.750 268.093,118.723 267.870,116.832 265.980 C 114.372 263.519,110.982 263.474,108.730 265.871 M281.294 265.581 C 279.146 267.729,278.781 270.697,279.055 283.790 L 279.297 295.315 265.577 295.314 C 250.024 295.312,248.438 295.846,248.438 301.081 C 248.438 306.078,249.145 306.250,269.667 306.250 C 286.473 306.250,288.149 306.128,289.332 304.821 C 291.687 302.219,291.236 268.034,288.817 265.761 C 286.574 263.654,283.299 263.576,281.294 265.581" />
        </svg>
        <div>
          <h4 className="text-slate-700">Start A Face Scan</h4>
          <p className="mt-1 text-sm font-light">Scan your vitals using your front camera.</p>
        </div>
      </button>
      <button
        type="button"
        onClick={() => {
          setScanType("finger");
          setModalOpen(true);
          setPage("startScan");
        }}
        className="w-full mb-8 p-4 pl-2 flex items-center justify-between space-x-2 rounded-3xl bg-neutral-100 text-slate-500 text-left"
      >
        <svg className="flex-shrink-0 w-20" fill="currentColor" viewBox="0 0 400 400">
          <path d="M102.947 1.832 C 89.883 6.284,79.885 17.743,77.356 31.161 C 76.261 36.970,76.261 363.030,77.356 368.839 C 79.922 382.454,90.517 394.366,103.676 398.430 C 111.631 400.887,288.369 400.887,296.324 398.430 C 309.483 394.366,320.078 382.454,322.644 368.839 C 323.160 366.101,323.438 320.697,323.438 238.920 L 323.438 113.210 321.520 111.293 C 318.983 108.756,315.392 108.756,312.855 111.293 L 310.938 113.210 310.938 230.824 L 310.938 348.438 231.214 348.438 L 151.491 348.438 149.574 350.355 C 147.037 352.892,147.037 356.483,149.574 359.020 L 151.491 360.938 231.321 360.938 L 311.150 360.938 310.650 364.585 C 309.023 376.453,299.558 385.772,287.335 387.539 C 280.923 388.467,119.077 388.467,112.665 387.539 C 100.442 385.772,90.977 376.453,89.350 364.585 L 88.850 360.938 108.914 360.938 L 128.977 360.938 130.895 359.020 C 133.432 356.483,133.432 352.892,130.895 350.355 L 128.977 348.438 109.020 348.438 L 89.063 348.438 89.063 191.939 C 89.063 19.764,88.722 31.663,93.865 23.900 C 96.698 19.623,101.872 15.540,107.031 13.510 C 111.099 11.909,275.948 10.903,286.838 12.412 C 296.783 13.790,304.374 19.252,308.461 27.971 L 310.547 32.422 310.938 61.443 C 311.379 94.272,311.277 93.750,317.252 93.750 C 323.413 93.750,323.438 93.622,323.438 61.778 C 323.438 23.680,321.145 16.321,306.159 6.313 C 296.063 -0.429,303.271 -0.003,199.722 0.020 L 108.203 0.040 102.947 1.832 M124.297 38.577 C 123.677 39.325,123.468 44.279,123.616 54.759 L 123.828 69.863 125.977 71.065 C 127.909 72.145,128.341 72.145,130.273 71.065 L 132.422 69.864 132.638 58.370 L 132.854 46.875 143.806 46.875 C 157.545 46.875,161.513 44.318,156.840 38.477 C 155.654 36.994,125.533 37.088,124.297 38.577 M239.871 39.011 C 236.633 45.063,239.727 46.875,253.299 46.875 L 264.063 46.875 264.063 58.159 C 264.063 68.934,264.141 69.498,265.799 70.659 C 268.110 72.278,270.561 72.196,272.145 70.446 C 273.791 68.627,274.109 40.046,272.500 38.438 C 270.699 36.637,240.861 37.161,239.871 39.011 M189.844 43.777 C 173.979 46.595,157.320 60.334,162.946 65.960 C 165.194 68.207,167.886 67.420,172.547 63.154 C 193.065 44.372,225.459 52.721,234.070 79.010 C 235.885 84.550,235.938 85.433,235.938 110.252 L 235.938 135.793 231.640 136.061 L 227.342 136.328 227.343 128.686 C 227.344 120.526,226.322 116.753,223.205 113.411 C 215.114 104.737,198.617 107.506,194.579 118.215 C 194.062 119.586,193.750 129.192,193.750 143.738 L 193.750 167.062 190.854 166.262 C 189.262 165.822,186.537 165.654,184.800 165.888 C 177.171 166.916,166.075 155.193,162.407 142.231 C 160.938 137.041,160.630 99.781,161.881 78.540 C 162.308 71.281,154.605 71.262,152.466 78.517 C 150.631 84.738,150.229 132.313,151.939 140.890 C 154.148 151.975,160.126 162.396,167.738 168.434 C 169.798 170.069,171.781 171.650,172.143 171.948 C 172.505 172.246,172.154 173.477,171.362 174.684 C 169.329 177.784,168.825 210.785,170.626 222.852 C 173.023 238.903,179.996 255.796,187.414 263.521 C 193.309 269.660,193.757 270.910,193.718 281.106 L 193.686 289.453 190.007 291.196 C 183.366 294.341,182.813 296.018,182.813 312.983 C 182.812 334.607,184.116 336.120,202.443 335.764 L 213.614 335.547 214.799 333.434 C 217.394 328.807,214.329 326.992,203.482 326.732 C 198.305 326.608,193.787 326.136,193.213 325.660 C 191.586 324.309,191.764 302.097,193.415 300.446 C 195.281 298.580,283.625 298.580,285.491 300.446 C 287.158 302.114,287.319 324.311,285.674 325.676 C 284.958 326.270,275.307 326.608,255.094 326.748 L 225.559 326.953 223.928 329.137 C 222.422 331.156,222.388 331.482,223.483 333.434 L 224.669 335.547 256.173 335.751 C 297.951 336.022,296.094 337.064,296.094 313.346 C 296.094 296.390,294.378 291.989,287.049 290.149 C 283.863 289.350,286.174 277.596,290.954 270.295 C 297.389 260.466,297.534 254.660,291.345 254.699 C 288.574 254.717,287.819 255.424,285.076 260.575 C 278.742 272.470,277.282 276.048,276.008 282.784 L 274.674 289.844 238.899 289.844 L 203.125 289.844 203.121 279.883 C 203.115 266.758,202.599 265.163,196.150 258.347 C 190.029 251.878,189.598 251.120,185.183 239.075 C 180.418 226.077,179.690 220.730,179.689 198.738 C 179.687 176.709,179.954 175.781,186.298 175.781 C 193.141 175.781,193.302 176.219,193.750 196.053 C 194.186 215.335,194.287 215.641,200.000 214.871 C 202.643 214.515,202.735 213.100,203.125 167.080 L 203.516 120.955 205.360 119.462 C 207.838 117.456,212.495 117.516,215.129 119.588 L 217.188 121.207 217.188 159.858 L 217.188 198.509 219.105 200.426 C 221.445 202.766,222.618 202.822,225.285 200.725 L 227.344 199.105 227.344 173.893 C 227.344 144.817,227.121 145.703,234.429 145.703 C 241.196 145.703,240.959 144.731,241.406 174.275 C 241.830 202.277,241.697 201.525,246.233 201.551 C 250.593 201.575,250.781 200.749,250.781 181.525 L 250.781 163.942 253.163 161.561 C 255.272 159.451,255.918 159.230,258.819 159.619 C 264.828 160.425,264.844 160.490,264.844 184.888 L 264.844 206.321 266.761 208.239 C 272.155 213.633,275.000 209.480,275.000 196.211 C 275.000 182.949,275.429 182.031,281.628 182.031 C 289.264 182.031,289.063 181.140,289.063 214.828 C 289.063 246.381,289.018 246.094,293.886 246.094 C 298.521 246.094,298.511 246.167,298.268 212.286 L 298.047 181.393 295.658 178.392 C 291.803 173.549,287.425 171.698,280.743 172.087 L 275.158 172.411 274.873 166.870 C 274.285 155.430,269.047 150.391,257.745 150.391 C 250.986 150.391,250.781 150.338,250.781 148.581 C 250.781 146.472,249.625 144.021,247.552 141.740 C 246.175 140.223,246.068 138.258,245.874 110.880 C 245.678 83.107,245.581 81.386,243.948 76.563 C 236.101 53.385,213.353 39.601,189.844 43.777 M191.406 60.067 C 180.291 62.567,170.513 72.685,168.066 84.218 C 166.659 90.847,166.918 133.525,168.403 139.826 C 171.008 150.878,178.510 158.209,182.813 153.906 C 184.927 151.792,184.777 149.503,182.312 146.271 C 177.607 140.102,177.344 138.252,177.344 111.349 C 177.344 88.717,177.467 86.449,178.901 82.615 C 183.846 69.399,201.487 65.031,212.119 74.390 C 217.695 79.299,219.531 84.057,219.531 93.598 C 219.531 102.594,220.475 104.688,224.533 104.688 C 228.800 104.688,229.688 102.924,229.688 94.439 C 229.688 71.005,212.277 55.373,191.406 60.067 M192.578 76.889 C 185.493 80.051,183.594 84.984,183.594 100.229 C 183.594 113.290,184.401 115.625,188.918 115.625 C 193.077 115.625,193.750 113.525,193.750 100.540 C 193.750 87.126,194.199 85.677,198.353 85.677 C 202.052 85.677,202.691 86.870,203.167 94.663 C 203.715 103.636,206.474 106.242,211.613 102.642 C 214.472 100.640,213.873 86.823,210.731 82.295 C 206.674 76.445,198.815 74.105,192.578 76.889 M124.730 160.804 C 122.834 162.899,122.791 190.648,124.681 192.539 C 125.750 193.607,128.088 193.753,141.273 193.571 L 156.621 193.359 157.693 191.121 C 160.109 186.077,157.496 184.800,144.253 184.549 L 132.813 184.333 132.813 173.147 C 132.813 163.302,132.641 161.806,131.384 160.668 C 129.497 158.960,126.340 159.025,124.730 160.804" />
        </svg>
        <div>
          <h4 className="text-slate-700">Start A Finger Scan</h4>
          <p className="mt-1 text-sm font-light">
            Scan your vitals by placing your finger on the rear camera.
          </p>
        </div>
      </button>
      <div className="mt-6 grid grid-cols-2 gap-6">
        <button
          type="button"
          onClick={() => navigate("/", { replace: true })}
          className="px-4 py-2 rounded-full bg-white text-slate-700 border border-slate-700 text-xl font-medium"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => setPage("patientData")}
          className="px-4 py-2 rounded-full bg-white text-slate-700 border border-slate-700 text-xl font-medium"
        >
          Back
        </button>
      </div>
    </div>
  ) : (
    currentPage === "startScan" && (
      <div>
        <Dialog
          open={isModalOpen}
          onClose={() => {}}
          className="fixed inset-0 bg-black/25 p-4 flex items-center justify-center"
        >
          <Dialog.Panel className="max-w-sm p-4 bg-white text-slate-700 rounded-lg">
            <Dialog.Description className="mb-2 text-lg">
              Please hand over the device to the Patient
            </Dialog.Description>
            <button
              type="button"
              onClick={() => setModalOpen(false)}
              className="block ml-auto mr-0 outline-none text-xl font-medium"
            >
              Ok
            </button>
          </Dialog.Panel>
        </Dialog>
        <h1 className="mb-6 text-slate-700 text-center text-3xl font-medium">Instructions</h1>
        {scanType === "face" ? (
          <div className="text-slate-700">
            <img
              className="mx-auto w-4/5 rounded-2xl"
              src={process.env.PUBLIC_URL + "/images/instruction-face.png"}
              alt="instruction"
            />
            <ul className="mt-6 mr-2 ml-8 list-disc text-sm">
              <li className="mb-3">
                The ambient light should be bright and consistent and must be white
              </li>
              <li className="mb-3">
                Keep your face and phone steady to avoid movements during the scan
              </li>
              <li className="mb-3">
                For light mode: Make sure your face is mostly covered by the blue circle
              </li>
            </ul>
          </div>
        ) : (
          scanType === "finger" && (
            <div className="text-slate-700">
              <img
                className="mx-auto w-4/5 rounded-2xl"
                src={process.env.PUBLIC_URL + "/images/instruction-finger.png"}
                alt="instruction"
              />
              <ul className="mt-6 mr-2 ml-8 list-disc text-sm">
                <li className="mb-3">
                  Place your finger on the rear primary camera and the flashlight
                </li>
                <li className="mb-3">Make sure the screen looks bright red in color</li>
                <li className="mb-3">
                  If the flash is not turned on then place some light behind the phone
                </li>
              </ul>
            </div>
          )
        )}
        <div className="mt-6 grid grid-cols-2 gap-6">
          <button
            type="button"
            onClick={() => {
              setScanType("");
              setModalOpen(false);
              setPage("scanType");
            }}
            disabled={loading}
            className="px-4 py-2 rounded-full bg-white text-slate-700 border border-slate-700 text-xl font-medium"
          >
            Back
          </button>
          <button
            type="button"
            onClick={getScanToken}
            disabled={loading}
            className="px-4 py-2 rounded-full bg-slate-700 text-white border border-slate-700 text-xl font-medium"
          >
            {loading ? (
              <svg className="mx-auto h-6 w-6 animate-spin" fill="currentColor" viewBox="0 0 50 50">
                <path d="M41.9 23.9c-.3-6.1-4-11.8-9.5-14.4-6-2.7-13.3-1.6-18.3 2.6-4.8 4-7 10.5-5.6 16.6 1.3 6 6 10.9 11.9 12.5 7.1 2 13.6-1.4 17.6-7.2-3.6 4.8-9.1 8-15.2 6.9-6.1-1.1-11.1-5.7-12.5-11.7-1.5-6.4 1.5-13.1 7.2-16.4 5.9-3.4 14.2-2.1 18.1 3.7 1 1.4 1.7 3.1 2 4.8.3 1.4.2 2.9.4 4.3.2 1.3 1.3 3 2.8 2.1 1.3-.8 1.2-2.5 1.1-3.8 0-.4.1.7 0 0z" />
              </svg>
            ) : (
              "Start Scan"
            )}
          </button>
        </div>
      </div>
    )
  );
};

export default SelectOption;
